import { useEffect, useRef, useState } from "react";
import Accordion from "react-bootstrap/Accordion";
import {
  FloatingLabel,
  Form,
  Button,
  Badge,
  ListGroup,
  ListGroupItem,
} from "react-bootstrap";
import BookSearchBar from "./BookSearchBar";
import NarrationButton from "./NarrationButton";
import "./AccordionComponent.css";
import "../common.css";

function BookListAccordion({
  bookList,
  deletable,
  removeBookAtIndex,
  ranked,
  scored,
  audioUrls = {},
  playbackDisabled = false,
}) {
  const audioRef = useRef(null);
  const playRequest = useRef({ id: 0 });
  const [activeBookId, setActiveBookId] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState(null);

  useEffect(() => {
    const audio = audioRef.current;
    const requests = playRequest.current;
    return () => {
      requests.id++;
      audio?.pause();
      audio?.removeAttribute("src");
      audio?.load();
    };
  }, []);

  useEffect(() => {
    if (!playbackDisabled) return;
    playRequest.current.id++;
    audioRef.current?.pause();
    setPlaying(false);
    setAudioLoading(false);
  }, [playbackDisabled]);

  const toggleNarration = async (book) => {
    const audio = audioRef.current;
    const bookId = book.narrationId || book.id || book.title;
    const audioUrl = audioUrls[bookId];
    if (!audio || !audioUrl || playbackDisabled) return;
    const requestId = ++playRequest.current.id;
    if (activeBookId === bookId && !audio.paused && audioError !== bookId) {
      audio.pause();
      setAudioLoading(false);
      return;
    }
    audio.pause();
    if (
      activeBookId !== bookId ||
      audio.getAttribute("src") !== audioUrl ||
      audioError === bookId
    ) {
      audio.src = audioUrl;
      audio.load();
    } else if (audio.ended) {
      audio.currentTime = 0;
    }
    setActiveBookId(bookId);
    setAudioError(null);
    setAudioLoading(true);
    try {
      await audio.play();
      if (playRequest.current.id === requestId) {
        setPlaying(true);
        setAudioLoading(false);
      }
    } catch {
      if (playRequest.current.id === requestId) {
        setPlaying(false);
        setAudioLoading(false);
        setAudioError(bookId);
      }
    }
  };

  return (
    <Accordion
      onSelect={(key) => {
        if (key !== activeBookId) {
          playRequest.current.id++;
          audioRef.current?.pause();
          setPlaying(false);
          setAudioLoading(false);
        }
      }}
    >
      <audio
        ref={audioRef}
        preload="none"
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onWaiting={() => setAudioLoading(true)}
        onPlaying={() => setAudioLoading(false)}
        onError={() => {
          setPlaying(false);
          setAudioLoading(false);
          setAudioError(activeBookId);
        }}
      />
      {bookList.map((book, index) => {
        const bookId = book.narrationId || book.id || book.title;
        return (
          <Accordion.Item eventKey={bookId} key={bookId}>
            <Accordion.Header
              className={ranked && scored ? "accordion-header-scored" : ""}
            >
              {ranked ? (
                <div
                  className={`accordion-title-row${
                    scored ? " accordion-title-row-scored" : ""
                  }`}
                >
                  <div className="ranking-number-container">{`${
                    index + 1
                  }.`}</div>
                  <span className="accordion-book-title">{book.title}</span>
                  {scored && (
                    <div className="accordion-badge-container">
                      <Badge className="accordion-badge">{book.score}</Badge>
                    </div>
                  )}
                </div>
              ) : (
                book.title
              )}
            </Accordion.Header>
            <Accordion.Body>
              <b>Author</b>
              <br />
              {book.author}
              <br />
              <br />
              <b>Page Count</b>
              <br />
              {book.pageCount}
              <br />
              <br />
              <div className="book-description-heading">
                <b>Description</b>
                {!deletable && (
                  <NarrationButton
                    title={book.title}
                    audioUrl={audioUrls[bookId]}
                    playing={activeBookId === bookId && playing}
                    loading={activeBookId === bookId && audioLoading}
                    disabled={playbackDisabled}
                    onClick={() => toggleNarration(book)}
                  />
                )}
              </div>
              {book.description}
              {audioError === bookId && (
                <div className="narration-playback-error" role="status">
                  Couldn’t play this narration. Please try again.
                </div>
              )}
              {deletable && (
                <div className="centered-container">
                  <Button
                    className="button-tertiary mt-4"
                    onClick={() => {
                      removeBookAtIndex(index);
                    }}
                  >
                    Delete book
                  </Button>
                </div>
              )}
            </Accordion.Body>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
}

function NewBookAccordion({
  newTitle,
  setNewTitle,
  newAuthor,
  setNewAuthor,
  newDescription,
  setNewDescription,
  newPageCount,
  setNewPageCount,
  addBook,
}) {
  const isStringEmptyOrSpaces = (str) => {
    return str.trim() === "";
  };
  const isBookValid = () => {
    return (
      !isStringEmptyOrSpaces(newTitle) &&
      !isStringEmptyOrSpaces(newAuthor) &&
      !isStringEmptyOrSpaces(newDescription) &&
      newDescription.length <= 10000 &&
      !isStringEmptyOrSpaces(newPageCount)
    );
  };

  return (
    <Accordion>
      <Accordion.Item eventKey="0">
        <Accordion.Header>
          <i>Add book to list...</i>
        </Accordion.Header>
        <Accordion.Body>
          <div className="mb-4 accordion-text">
            Look for a book to auto-fill...
          </div>
          <div className="mb-4">
            <BookSearchBar
              setNewTitle={setNewTitle}
              setNewAuthor={setNewAuthor}
              setNewDescription={setNewDescription}
              setNewPageCount={setNewPageCount}
            />
          </div>
          <div className="mb-4 accordion-text">
            ...or fill the data manually:
          </div>

          <FloatingLabel
            controlId="newBookTitle"
            label="Title"
            className="mb-4"
          >
            <Form.Control
              type="text"
              placeholder="Book's Title"
              className="input-box"
              value={newTitle}
              onChange={(e) => {
                setNewTitle(e.target.value);
              }}
            />
          </FloatingLabel>
          <FloatingLabel
            controlId="newBookAuthor"
            label="Author"
            className="mb-4"
          >
            <Form.Control
              type="text"
              placeholder="Book's Author"
              className="input-box"
              value={newAuthor}
              onChange={(e) => {
                setNewAuthor(e.target.value);
              }}
            />
          </FloatingLabel>
          <FloatingLabel
            controlId="newBookPageCount"
            label="Page Count"
            className="mb-4"
          >
            <Form.Control
              type="text"
              placeholder="Book's Number of Pages"
              className="input-box"
              value={newPageCount}
              onChange={(e) => {
                setNewPageCount(e.target.value);
              }}
            />
          </FloatingLabel>
          <FloatingLabel
            label="Description"
            controlId="newBookDescription"
            className="mb-4"
          >
            <Form.Control
              as="textarea"
              maxLength={10000}
              placeholder="Book's Description"
              className="input-box"
              style={{ height: "9rem" }}
              value={newDescription}
              onChange={(e) => {
                setNewDescription(e.target.value);
              }}
            />
          </FloatingLabel>
          <div className="centered-container">
            <Button
              className="button-tertiary mb-2"
              onClick={addBook}
              disabled={!isBookValid()}
            >
              Add book
            </Button>
          </div>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
}

function RankersAccordion({ rankings, closed }) {
  if (closed) {
    return (
      <Accordion>
        {rankings.map((r, index) => (
          <Accordion.Item
            eventKey={`${r.user}:${index}`}
            key={`${r.user}:${index}`}
          >
            <Accordion.Header>{r.user}</Accordion.Header>
            <Accordion.Body>
              {Object.keys(r.rankings)
                .map(Number)
                .sort((a, b) => a - b)
                .map((key) => (
                  <div key={key}>
                    <b>{`${key}.`}</b>
                    <br />
                    {r.rankings[key]}
                    <br />
                    <br />
                  </div>
                ))}
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>
    );
  }
  return (
    <ListGroup>
      {rankings.map((r, index) => (
        <ListGroupItem>{r.user}</ListGroupItem>
      ))}
    </ListGroup>
  );
}

export { BookListAccordion, NewBookAccordion, RankersAccordion };
